import React, { useState, useEffect } from "react";
import {
  TextField,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  CircularProgress,
  Container,
  Tabs,
  Tab,
  Button,
} from "@mui/material";
import { db } from "../firebase-config";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";

const Home = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState(["all"]);
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");

  // Get query parameters from URL
  const searchQuery = searchParams.get("q") || "";

  // Detect current user
  useEffect(() => {
    const auth = getAuth();
    onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
  }, []);

  // Fetch Categories from Firebase
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const catalogRef = collection(db, "Categories");
        const snapshot = await getDocs(catalogRef);
        const categoryList = snapshot.docs.map((doc) => doc.data().name);
        setCategories(["all", ...categoryList]);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  // Fetch Products based on user
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsRef = collection(db, "products");
        let q = query(productsRef, where("visibility.public", "==", true));

        // If user is logged in, allow them to see their own private products
        if (currentUser) {
          q = query(
            productsRef,
            where("visibility.public", "==", true) // Public products
          );

          const userEmailQuery = query(productsRef, where("ownerEmail", "==", currentUser.email));
          const userCompanyQuery = query(productsRef, where("ownerCompany", "==", currentUser.company));

          // Fetch private products belonging to user
          const [publicSnapshot, emailSnapshot, companySnapshot] = await Promise.all([
            getDocs(q),
            getDocs(userEmailQuery),
            getDocs(userCompanyQuery),
          ]);

          const productList = [
            ...publicSnapshot.docs,
            ...emailSnapshot.docs,
            ...companySnapshot.docs,
          ].map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          setProducts(productList);
        } else {
          const snapshot = await getDocs(q);
          const productList = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setProducts(productList);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [currentUser]);

  // Filter products based on search input & category
  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Function to update URL parameters
  const updateURL = (param, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(param, value);
    } else {
      newParams.delete(param);
    }
    navigate(`?${newParams.toString()}`, { replace: true });
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Available Products
      </Typography>

      {/* Search Field */}
      <TextField
        label="Search Products"
        variant="outlined"
        fullWidth
        margin="normal"
        value={searchQuery}
        onChange={(e) => updateURL("q", e.target.value)}
      />

      {/* Category Tabs */}
      <Tabs
        value={selectedCategory}
        onChange={(event, newValue) => setSelectedCategory(newValue)}
        sx={{ mb: 3 }}
      >
        {categories.map((category) => (
          <Tab key={category} label={category} value={category} />
        ))}
      </Tabs>

      {loading ? (
        <CircularProgress />
      ) : (
        <Grid container spacing={3}>
          {filteredProducts.length > 0 ? (
            filteredProducts.map((product) => (
              <Grid item key={product.id} xs={12} sm={6} md={4} lg={3}>
                <Card>
                  <CardMedia
                    component="img"
                    height="200"
                    image={product.imageUrl || "https://via.placeholder.com/200"}
                    alt={product.name}
                  />
                  <CardContent>
                    <Typography variant="h6">{product.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {product.description ? product.description.slice(0, 60) + "..." : "No description available"}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      sx={{ mt: 2 }}
                      onClick={() => navigate(`/products/${product.id}`)}
                    >
                      View Product
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Typography variant="body1">No products found.</Typography>
          )}
        </Grid>
      )}
    </Container>
  );
};

export default Home;
